import SEO from "@/components/SEO";

export default function Terms() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Terms of Service - Stanford Logistics Management"
        description="Terms of Service for Stanford Logistics Management. Read our terms and conditions for using our website and services."
      />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary via-primary/95 to-primary/90 text-primary-foreground py-16 lg:py-20">
        <div className="container">
          <div className="max-w-3xl">
            <h1 className="text-4xl font-bold tracking-tight sm:text-5xl mb-4">
              Terms of Service
            </h1>
            <p className="text-lg text-primary-foreground/90">
              Last updated: November 17, 2025
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12 lg:py-16 bg-background">
        <div className="container">
          <div className="max-w-4xl mx-auto space-y-12">
            
            {/* Agreement to Terms */}
            <div className="space-y-4">
              <h2 className="text-3xl font-bold text-foreground">Agreement to Terms</h2>
              <p className="text-foreground/80 leading-relaxed text-lg">
                By accessing or using the Stanford Logistics Management website (the "Site"), you agree to be 
                bound by these Terms of Service ("Terms"). If you do not agree to these Terms, please do not 
                use the Site.
              </p>
            </div>

            {/* Use of the Site */}
            <div className="space-y-6">
              <h2 className="text-3xl font-bold text-foreground">Use of the Site</h2>
              
              <div className="space-y-4">
                <h3 className="text-2xl font-semibold text-foreground">Permitted Use</h3>
                <p className="text-foreground/80 leading-relaxed text-lg">
                  You may use the Site for lawful purposes only and in accordance with these Terms. You agree not to:
                </p>
                <ul className="list-disc list-inside space-y-2 text-foreground/80 text-lg ml-4">
                  <li>Use the Site in any way that violates any applicable federal, state, local, or international law or regulation</li>
                  <li>Engage in any conduct that restricts or inhibits anyone's use or enjoyment of the Site</li>
                  <li>Attempt to gain unauthorized access to any portion of the Site or any systems or networks connected to the Site</li>
                  <li>Use any automated system, including robots, spiders, or scrapers, to access the Site</li>
                  <li>Transmit any viruses, malware, or other malicious code</li>
                </ul>
              </div>

              <div className="space-y-4">
                <h3 className="text-2xl font-semibold text-foreground">Intellectual Property</h3>
                <p className="text-foreground/80 leading-relaxed text-lg">
                  The Site and its entire contents, features, and functionality (including but not limited to all 
                  information, software, text, displays, images, and the design, selection, and arrangement thereof) 
                  are owned by Stanford Logistics Management, its licensors, or other providers of such material and 
                  are protected by United States and international copyright, trademark, patent, trade secret, and 
                  other intellectual property or proprietary rights laws.
                </p>
              </div>
            </div>

            {/* User Content */}
            <div className="space-y-4">
              <h2 className="text-3xl font-bold text-foreground">User Content</h2>
              <p className="text-foreground/80 leading-relaxed text-lg">
                If you submit any content to the Site, including through contact forms or other communications, 
                you grant Stanford Logistics Management a non-exclusive, royalty-free, perpetual, irrevocable, and 
                fully sublicensable right to use, reproduce, modify, adapt, publish, translate, create derivative 
                works from, distribute, and display such content throughout the world in any media.
              </p>
            </div>

            {/* Third-Party Links */}
            <div className="space-y-4">
              <h2 className="text-3xl font-bold text-foreground">Third-Party Links</h2>
              <p className="text-foreground/80 leading-relaxed text-lg">
                The Site may contain links to third-party websites or services that are not owned or controlled by 
                Stanford Logistics Management. We have no control over, and assume no responsibility for, the content, 
                privacy policies, or practices of any third-party websites or services. You acknowledge and agree that 
                Stanford Logistics Management shall not be responsible or liable, directly or indirectly, for any 
                damage or loss caused or alleged to be caused by or in connection with the use of or reliance on any 
                such content, goods, or services available on or through any such websites or services.
              </p>
            </div>

            {/* Disclaimer of Warranties */}
            <div className="space-y-4">
              <h2 className="text-3xl font-bold text-foreground">Disclaimer of Warranties</h2>
              <p className="text-foreground/80 leading-relaxed text-lg font-semibold">
                THE SITE IS PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS, WITHOUT ANY WARRANTIES OF ANY KIND, 
                EITHER EXPRESS OR IMPLIED. STANFORD LOGISTICS MANAGEMENT DISCLAIMS ALL WARRANTIES, EXPRESS OR IMPLIED, 
                INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, 
                AND NON-INFRINGEMENT.
              </p>
              <p className="text-foreground/80 leading-relaxed text-lg">
                Stanford Logistics Management does not warrant that the Site will be uninterrupted, secure, or 
                error-free, that defects will be corrected, or that the Site or the server that makes it available 
                are free of viruses or other harmful components.
              </p>
            </div>

            {/* Limitation of Liability */}
            <div className="space-y-4">
              <h2 className="text-3xl font-bold text-foreground">Limitation of Liability</h2>
              <p className="text-foreground/80 leading-relaxed text-lg font-semibold">
                IN NO EVENT SHALL STANFORD LOGISTICS MANAGEMENT, ITS AFFILIATES, OR THEIR LICENSORS, SERVICE PROVIDERS, 
                EMPLOYEES, AGENTS, OFFICERS, OR DIRECTORS BE LIABLE FOR DAMAGES OF ANY KIND, UNDER ANY LEGAL THEORY, 
                ARISING OUT OF OR IN CONNECTION WITH YOUR USE, OR INABILITY TO USE, THE SITE, INCLUDING ANY DIRECT, 
                INDIRECT, SPECIAL, INCIDENTAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO 
                PERSONAL INJURY, PAIN AND SUFFERING, EMOTIONAL DISTRESS, LOSS OF REVENUE, LOSS OF PROFITS, LOSS OF 
                BUSINESS OR ANTICIPATED SAVINGS, LOSS OF USE, LOSS OF GOODWILL, LOSS OF DATA, AND WHETHER CAUSED BY 
                TORT (INCLUDING NEGLIGENCE), BREACH OF CONTRACT, OR OTHERWISE, EVEN IF FORESEEABLE.
              </p>
            </div>

            {/* Indemnification */}
            <div className="space-y-4">
              <h2 className="text-3xl font-bold text-foreground">Indemnification</h2>
              <p className="text-foreground/80 leading-relaxed text-lg">
                You agree to defend, indemnify, and hold harmless Stanford Logistics Management, its affiliates, 
                licensors, and service providers, and its and their respective officers, directors, employees, 
                contractors, agents, licensors, suppliers, successors, and assigns from and against any claims, 
                liabilities, damages, judgments, awards, losses, costs, expenses, or fees (including reasonable 
                attorneys' fees) arising out of or relating to your violation of these Terms or your use of the Site.
              </p>
            </div>

            {/* Governing Law and Jurisdiction */}
            <div className="space-y-4">
              <h2 className="text-3xl font-bold text-foreground">Governing Law and Jurisdiction</h2>
              <p className="text-foreground/80 leading-relaxed text-lg">
                These Terms shall be governed by and construed in accordance with the laws of the State of Georgia, 
                without regard to its conflict of law provisions. You agree to submit to the personal jurisdiction of 
                the state and federal courts located in Georgia for the purpose of litigating all such claims or 
                disputes.
              </p>
            </div>

            {/* Changes to Terms */}
            <div className="space-y-4">
              <h2 className="text-3xl font-bold text-foreground">Changes to Terms</h2>
              <p className="text-foreground/80 leading-relaxed text-lg">
                We reserve the right to modify or replace these Terms at any time. If a revision is material, we will 
                provide at least 30 days' notice prior to any new terms taking effect. What constitutes a material 
                change will be determined at our sole discretion. By continuing to access or use the Site after those 
                revisions become effective, you agree to be bound by the revised terms.
              </p>
            </div>

            {/* Severability */}
            <div className="space-y-4">
              <h2 className="text-3xl font-bold text-foreground">Severability</h2>
              <p className="text-foreground/80 leading-relaxed text-lg">
                If any provision of these Terms is held to be invalid or unenforceable, such provision shall be struck 
                and the remaining provisions shall be enforced to the fullest extent under law.
              </p>
            </div>

            {/* Entire Agreement */}
            <div className="space-y-4">
              <h2 className="text-3xl font-bold text-foreground">Entire Agreement</h2>
              <p className="text-foreground/80 leading-relaxed text-lg">
                These Terms constitute the entire agreement between you and Stanford Logistics Management regarding 
                your use of the Site and supersede all prior and contemporaneous written or oral agreements between 
                you and Stanford Logistics Management.
              </p>
            </div>

            {/* Contact Information */}
            <div className="space-y-4 pb-8">
              <h2 className="text-3xl font-bold text-foreground">Contact Information</h2>
              <p className="text-foreground/80 leading-relaxed text-lg">
                If you have any questions about these Terms, please contact us at:
              </p>
              <div className="bg-muted/30 p-6 rounded-lg border border-border">
                <p className="text-foreground font-semibold text-lg mb-3">Stanford Logistics Management</p>
                <p className="text-foreground/80 text-lg">
                  Email: <a href="mailto:info@stanfordlogistics.net" className="text-accent hover:underline font-medium">info@stanfordlogistics.net</a>
                </p>
                <p className="text-foreground/80 text-lg">
                  Phone: <a href="tel:+12014703182" className="text-accent hover:underline font-medium">201-470-3182</a>
                </p>
                <p className="text-foreground/80 text-lg">
                  Location: Savannah, GA
                </p>
              </div>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
}
